import { useState } from "react";
import { AlertTriangle, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmt } from "../lib/api";

const Box = ({ label, value, cls = "", testId }) => (
  <div className="p-4 rounded-md bg-[#090D16] border border-[#1E293B]" data-testid={testId}>
    <div className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{label}</div>
    <div className={`font-display text-2xl font-extrabold ${cls}`}>{value}</div>
  </div>
);

export default function CapacityDeficit({ check, backlog, activeDays, onApply, busy }) {
  const [open, setOpen] = useState(false);
  if (!check || check.deficit_hours <= 0) return null;
  const red = check.deficit_hours > 0.2 * check.capacity_hours;
  const tone = red ? "border-[#EF4444] text-[#EF4444] bg-[#EF4444]/10" : "border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10";
  const perDay = activeDays ? Math.ceil((check.deficit_hours / activeDays) * 2 - 1e-9) / 2 : 0;
  return (
    <>
      <button data-testid="capacity-deficit-badge" onClick={() => setOpen(true)}
        className={`h-8 px-3 rounded-md border font-mono text-xs font-bold flex items-center gap-2 hover:brightness-125 active:scale-[0.98] ${tone}`}>
        <AlertTriangle className="w-3.5 h-3.5" />CAPACITY DEFICIT: +{check.deficit_hours.toFixed(1)} HRS NEEDED
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1623] border-[#334155] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="capacity-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">Labor Gap</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">Production time for this week's par-restock builds (T2 prep + T3 assembly) compared with configured capacity.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3" data-testid="capacity-summary">
            <Box label="HOURS NEEDED" value={check.required_hours.toFixed(1)} testId="capacity-needed" />
            <Box label="HOURS CONFIGURED" value={check.capacity_hours.toFixed(1)} testId="capacity-configured" />
            <Box label="LABOR SHORTAGE" value={`+${check.deficit_hours.toFixed(1)}`} cls={red ? "text-[#EF4444]" : "text-[#F59E0B]"} testId="capacity-gap" />
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#F59E0B] mt-2">UNSCHEDULED PAR BACKLOG</div>
          <div className="border border-[#1E293B] rounded-md divide-y divide-[#1E293B]" data-testid="capacity-backlog">
            {backlog.map((b) => (
              <div key={`${b.item_id}-${b.tier}`} data-testid={`capacity-backlog-${b.sku}`} className="flex justify-between gap-3 px-3 py-2 text-sm">
                <div className="min-w-0"><div className="truncate">{b.name}</div><div className="font-mono text-[11px] text-[#6B7280]">{b.sku} · {b.tier === 2 ? "T2 PREP" : "T3 ASSEMBLY"}</div></div>
                <div className="text-right font-mono shrink-0"><div className="font-bold">{fmt(b.qty)} units</div><div className="text-[11px] text-[#9CA3AF]">{(b.minutes / 60).toFixed(1)} hrs</div></div>
              </div>
            ))}
            {!backlog.length && <div className="px-3 py-3 text-xs text-[#6B7280]">Nothing left unscheduled.</div>}
          </div>
          {check.untimed?.length > 0 && (
            <div className="text-xs text-[#F59E0B]" data-testid="capacity-untimed">No production time set (not counted in hours): {check.untimed.join(", ")}</div>
          )}
          <button data-testid="capacity-apply-btn" disabled={busy || !activeDays} onClick={async () => { await onApply(); setOpen(false); }}
            className="mt-2 h-12 w-full rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center justify-center gap-2 disabled:opacity-40">
            <Zap className="w-4 h-4" />{busy ? "Re-planning…" : `Apply Required Hours to Schedule (+${perDay.toFixed(1)} h / active day)`}
          </button>
          <div className="text-[11px] text-[#6B7280] -mt-1">This week only, capped at 24 h per day. Re-plans straight away. The Master Controls default is unchanged.</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
